import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Users, CalendarCheck, FlaskConical, TestTube2, FileBarChart,
  Megaphone, Settings, Bell, ChevronDown, Menu, X, UserPlus,
  ClipboardList, Send, FileText, MessageCircle, BarChart3, Package,
  DollarSign, Building2, ListTree, Percent, Star,
  MessageSquare, Wrench, UserCog, Cpu, GitBranch, Shield, Link2,
  LogOut, Check, Beaker, LineChart, MapPin, ScanLine, CreditCard, Wallet,
  Globe, LayoutDashboard, Sliders, Search, Image as ImageIcon, Inbox
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useTenantBranding } from '../context/TenantBrandingContext';
import { useSubscription } from '../context/SubscriptionContext';
import SubscriptionBanner from './SubscriptionBanner';
import { chatAPI, bookingsAPI } from '../services/api';
import { getSocket } from '../services/socket';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, getUserInitials, canAccessPage } = useAuth();
  const { branches, selectedBranch, switchBranch, canSwitchBranch } = useBranch();
  const { logoUrl, labNameAr } = useTenantBranding();
  const { canAccessFeature } = useSubscription();
  
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [scanValue, setScanValue] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showBranchSelector, setShowBranchSelector] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatNotifications, setChatNotifications] = useState([]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );
  
  const navRef = useRef(null);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);
  const branchRef = useRef(null);
  const unreadReqRef = useRef({ inFlight: false, lastAt: 0, timer: null });
  const scanInputRef = useRef(null);
  const scanVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const scanRafRef = useRef(null);
  const lastDetectedRef = useRef({ code: '', at: 0 });

  useEffect(() => {
    function handleClickOutside(event) {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
        setShowBranchSelector(false);
      }
      if (branchRef.current && !branchRef.current.contains(event.target)) {
        setShowBranchSelector(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnread = useCallback(async (force = false) => {
    const minGapMs = 6000; // prevent burst calls from multiple socket events
    if (!force && (Date.now() - unreadReqRef.current.lastAt) < minGapMs) return;
    if (unreadReqRef.current.inFlight) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    unreadReqRef.current.inFlight = true;
    try {
      const activeThreadId = localStorage.getItem('chat_active_thread_id');
      const isViewingChat = window.location.pathname === '/lab/chat';
      const res = await chatAPI.getUnreadSummary(
        isViewingChat && activeThreadId ? { active_thread_id: activeThreadId } : undefined
      );
      const data = res.data?.data || {};
      unreadReqRef.current.lastAt = Date.now();
      setUnreadChatCount(data.unread_count || 0);
      setChatNotifications(data.items || []);
    } catch (error) {
      unreadReqRef.current.lastAt = Date.now();
      setUnreadChatCount(0);
      setChatNotifications([]);
    } finally {
      unreadReqRef.current.inFlight = false;
    }
  }, []);

  useEffect(() => {
    let active = true;
    const safeLoad = async (force = false) => {
      if (!active) return;
      await loadUnread(force);
    };

    const scheduleRefresh = () => {
      if (unreadReqRef.current.timer) clearTimeout(unreadReqRef.current.timer);
      unreadReqRef.current.timer = setTimeout(() => {
        safeLoad(true);
      }, 400);
    };

    safeLoad(true);

    const socket = getSocket();
    const refreshUnread = () => scheduleRefresh();

    // Polling is only a fallback when socket is unavailable/disconnected.
    const timer = setInterval(() => {
      const disconnected = !socket || socket.connected !== true;
      if (disconnected) safeLoad(false);
    }, 120000);

    const onVisibility = () => {
      if (!document.hidden) safeLoad(false);
    };
    document.addEventListener('visibilitychange', onVisibility);

    if (socket) {
      socket.on('chat:thread-updated', refreshUnread);
      socket.on('chat:new-message', refreshUnread);
      socket.on('chat:unread-updated', refreshUnread);
      socket.on('chat:read-updated', refreshUnread);
    }
    return () => {
      active = false;
      clearInterval(timer);
      if (unreadReqRef.current.timer) clearTimeout(unreadReqRef.current.timer);
      document.removeEventListener('visibilitychange', onVisibility);
      if (socket) {
        socket.off('chat:thread-updated', refreshUnread);
        socket.off('chat:new-message', refreshUnread);
        socket.off('chat:unread-updated', refreshUnread);
        socket.off('chat:read-updated', refreshUnread);
      }
    };
  }, [loadUnread]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Page key mapping for permission checks
  const pageKeyMap = {
    '/': 'dashboard',
    '/customers/new': 'new_customer',
    '/customers': 'customers',
    '/bookings/new': 'new_booking',
    '/bookings': 'bookings',
    '/bookings/home-visits': 'bookings',
    '/bookings/approve': 'approve_results',
    '/lab/results': 'lab_results',
    '/tests': 'tests',
    '/tests/microbiology-dictionary': 'tests',
    '/tests/packages': 'test_packages',
    '/tests/categories': 'test_categories',
    '/reports/income': 'income_report',
    '/reports/daily': 'daily_income',
    '/reports/sales': 'sales_analysis',
    '/marketing/coupons': 'coupons',
    '/marketing/whatsapp': 'whatsapp_marketing',
    '/settings/staff': 'staff_management',
    '/settings/branches': 'branches',
    '/settings/lab': 'lab_settings',
    '/settings/devices': 'devices',
    '/settings/analyzer-mappings': 'analyzer_mappings',
    '/qc/materials': 'qc_materials',
    '/qc/daily-entry': 'qc_daily_entry',
    '/qc/dashboard': 'qc_dashboard',
  };

  // Check if user can access a path
  const canAccessPath = (path) => {
    const pageKey = pageKeyMap[path];
    if (!pageKey) return true; // Allow access to pages not in the map
    return canAccessPage(pageKey);
  };

  // Menu structure with paths
  const menuItems = [
    {
      id: 'home',
      label: 'الرئيسية',
      icon: Home,
      path: '/',
      pageKey: 'dashboard',
      hasDropdown: false,
    },
    {
      id: 'customers',
      label: 'العملاء',
      icon: Users,
      hasDropdown: true,
      subItems: [
        { id: 'new-customer', label: 'تسجيل عميل جديد', icon: UserPlus, path: '/customers/new', pageKey: 'new_customer' },
        { id: 'customers-list', label: 'العملاء', icon: Users, path: '/customers', pageKey: 'customers' },
      ],
    },
    {
      id: 'bookings',
      label: 'الحجوزات',
      icon: CalendarCheck,
      hasDropdown: true,
      subItems: [
        { id: 'new-booking', label: 'حجز جديد', icon: ClipboardList, path: '/bookings/new', pageKey: 'new_booking' },
        { id: 'bookings-list', label: 'قائمة الحجوزات', icon: CalendarCheck, path: '/bookings', pageKey: 'bookings' },
        { id: 'home-visits-list', label: 'الزيارات المنزلية', icon: MapPin, path: '/bookings/home-visits', pageKey: 'bookings' },
        { id: 'contractor-bookings', label: 'حجوزات المختبرات المتعاقدة', icon: Building2, path: '/bookings/contractor' },
        { id: 'approve-results', label: 'موافقة وارسال النتائج', icon: Send, path: '/bookings/approve', pageKey: 'approve_results' },
      ],
    },
    {
      id: 'lab-operations',
      label: 'العمليات',
      icon: FlaskConical,
      hasDropdown: true,
      subItems: [
        { id: 'test-results', label: 'نتائج التحاليل', icon: FileText, path: '/lab/results', pageKey: 'lab_results' },
        { id: 'results-analysis', label: 'تحليل النتائج', icon: BarChart3, path: '/lab/analysis', featureKey: 'results_analysis' },
        { id: 'lab-chat', label: 'محادثات المختبر', icon: MessageCircle, path: '/lab/chat', featureKey: 'lab_chat' },
      ],
    },
    {
      id: 'tests',
      label: 'التحاليل',
      icon: TestTube2,
      hasDropdown: true,
      subItems: [
        { id: 'all-tests', label: 'التحاليل', icon: TestTube2, path: '/tests', pageKey: 'tests' },
        { id: 'test-pricing', label: 'تحديد الأسعار', icon: DollarSign, path: '/tests/pricing', pageKey: 'tests' },
        { id: 'micro-dictionary', label: 'Microbiology Dictionary', icon: Beaker, path: '/tests/microbiology-dictionary', pageKey: 'tests' },
        { id: 'test-packages', label: 'باقات التحاليل', icon: Package, path: '/tests/packages', pageKey: 'test_packages' },
        { id: 'contractor-prices', label: 'أسعار المختبرات المتعاقدة', icon: Building2, path: '/tests/contractor-prices' },
        { id: 'categories', label: 'الفئات', icon: ListTree, path: '/tests/categories', pageKey: 'test_categories' },
      ],
    },
    {
      id: 'reports',
      label: 'التقارير',
      icon: FileBarChart,
      hasDropdown: true,
      subItems: [
        { id: 'income-reports', label: 'تقارير الدخل', icon: FileText, path: '/reports/income', pageKey: 'income_report' },
        { id: 'daily-income', label: 'الدخل اليومي', icon: DollarSign, path: '/reports/daily', pageKey: 'daily_income' },
        { id: 'sales-analysis', label: 'تحليل المبيعات', icon: BarChart3, path: '/reports/sales', pageKey: 'sales_analysis', featureKey: 'sales_analysis' },
        { id: 'zatca-log', label: 'سجل فواتير ZATCA', icon: FileText, path: '/reports/zatca-log' },
      ],
    },
    {
      id: 'qc',
      label: 'مراقبة الجودة (QC)',
      icon: Beaker,
      hasDropdown: true,
      featureKey: 'qc_module',
      subItems: [
        { id: 'qc-materials', label: 'إعداد الكنترولات', icon: Beaker, path: '/qc/materials', featureKey: 'qc_module' },
        { id: 'qc-daily-entry', label: 'إدخال نتائج الجودة اليومية', icon: ClipboardList, path: '/qc/daily-entry', featureKey: 'qc_module' },
        { id: 'qc-dashboard', label: 'لوحة المراقبة والتقارير', icon: LineChart, path: '/qc/dashboard', featureKey: 'qc_module' },
      ],
    },
    {
      id: 'marketing',
      label: 'التسويق',
      icon: Megaphone,
      hasDropdown: true,
      subItems: [
        { id: 'coupons', label: 'اكواد الخصم', icon: Percent, path: '/marketing/coupons', pageKey: 'coupons', featureKey: 'coupons' },
        { id: 'reviews', label: 'مراجعات العملاء', icon: Star, path: '/marketing/reviews' },
        { id: 'whatsapp-marketing', label: 'التسويق عبر الواتس', icon: MessageSquare, path: '/marketing/whatsapp', pageKey: 'whatsapp_marketing', featureKey: 'whatsapp_marketing' },
      ],
    },
    // Per-tenant marketing website — gated by has_website (Advanced+ plan)
    {
      id: 'website',
      label: 'موقع المختبر',
      icon: Globe,
      hasDropdown: true,
      featureKey: 'has_website',
      subItems: [
        { id: 'website-builder', label: 'محرّر الموقع', icon: LayoutDashboard, path: '/settings/website' },
        { id: 'website-site-settings', label: 'إعدادات الموقع', icon: Sliders, path: '/settings/website/site-settings' },
        { id: 'website-seo', label: 'إعدادات SEO', icon: Search, path: '/settings/website/seo' },
        { id: 'website-media', label: 'مكتبة الوسائط', icon: ImageIcon, path: '/settings/website/media' },
        { id: 'website-blog', label: 'المدونة', icon: FileText, path: '/settings/website/blog' },
        { id: 'website-offers', label: 'العروض', icon: Percent, path: '/settings/website/offers' },
        { id: 'website-messages', label: 'رسائل التواصل', icon: Inbox, path: '/settings/website/messages' },
        { id: 'website-domain', label: 'الدومين والنشر', icon: Link2, path: '/settings/website/domain' },
      ],
    },
  ];

  // Settings menu
  const settingsMenu = {
    id: 'settings',
    label: 'الإعدادات',
    icon: Settings,
    hasDropdown: true,
    subItems: [
      { id: 'lab-settings', label: 'إعدادات المختبر', icon: Wrench, path: '/settings/lab', pageKey: 'lab_settings' },
      { id: 'staff-management', label: 'إدارة الموظفين', icon: UserCog, path: '/settings/staff', pageKey: 'staff_management' },
      { id: 'devices', label: 'أجهزة المختبر', icon: Cpu, path: '/settings/devices', pageKey: 'devices', featureKey: 'device_integration' },
      { id: 'analyzer-mappings', label: 'ربط الأجهزة بالتحاليل', icon: Link2, path: '/settings/analyzer-mappings', pageKey: 'analyzer_mappings', featureKey: 'device_integration' },
      { id: 'branches', label: 'فروع المختبر', icon: GitBranch, path: '/settings/branches', pageKey: 'branches' },
      { id: 'contractor-labs', label: 'المختبرات المتعاقدة', icon: Building2, path: '/settings/contractor-labs', featureKey: 'contractor_labs' },
      { id: 'contractor-accounts', label: 'حسابات المتعاقدين', icon: Wallet, path: '/settings/contractor-accounts', featureKey: 'contractor_labs' },
      { id: 'insurance', label: 'شركات التأمين', icon: Shield, path: '/settings/insurance', featureKey: 'insurance_companies' },
    ],
  };

  // Get user display info (needed before menu items)
  const userName = user?.name || 'مستخدم';
  const userRole = user?.role || 'user';
  const userInitials = getUserInitials ? getUserInitials() : userName.charAt(0);

  // Filter menu items based on permissions and feature gates
  const filterMenuByPermissions = (items) => {
    return items
      .map(item => {
        // Check top-level feature gate (e.g. entire QC section)
        if (item.featureKey && !canAccessFeature(item.featureKey)) return null;

        if (item.hasDropdown && item.subItems) {
          // Filter sub items based on permissions and feature gates
          const filteredSubItems = item.subItems.filter(subItem => {
            // Check feature gate first
            if (subItem.featureKey && !canAccessFeature(subItem.featureKey)) return false;
            // Then check page permission
            if (subItem.pageKey && !canAccessPage(subItem.pageKey)) return false;
            return true;
          });

          // Only include menu if it has accessible sub items
          if (filteredSubItems.length === 0) return null;

          return { ...item, subItems: filteredSubItems };
        } else {
          // Check permission for single item
          if (item.pageKey && !canAccessPage(item.pageKey)) return null;
          return item;
        }
      })
      .filter(Boolean); // Remove null items
  };

  // Filtered main menus + settings (separate icon-only trigger)
  const mainMenuItems = filterMenuByPermissions(menuItems);
  const settingsMenuFiltered = filterMenuByPermissions([settingsMenu])[0] || null;

  const notifications = chatNotifications.map((n) => ({
    id: n._id,
    text: `${n.sender_name}: ${n.message}`,
    time: new Date(n.sent_at).toLocaleString('ar-SA-u-ca-gregory', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }),
    unread: true,
    thread_id: n.thread_id,
    important: n.is_important === true,
  }));

  const isActive = (item) => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.subItems) {
      return item.subItems.some(sub => location.pathname === sub.path);
    }
    return false;
  };

  const handleMenuClick = (item) => {
    if (item.hasDropdown) {
      setOpenDropdown(openDropdown === item.id ? null : item.id);
    } else if (item.path) {
      navigate(item.path);
      setOpenDropdown(null);
    }
  };

  const handleSubItemClick = (subItem) => {
    if (subItem.path) {
      navigate(subItem.path);
    }
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBranchSelect = (branch) => {
    switchBranch(branch);
    setShowBranchSelector(false);
    setShowUserMenu(false);
  };

  const handleOpenScan = () => {
    setScanValue('');
    setScanError('');
    setCameraError('');
    setShowScanModal(true);
  };

  const stopCameraScan = useCallback(() => {
    if (scanRafRef.current) {
      cancelAnimationFrame(scanRafRef.current);
      scanRafRef.current = null;
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    setShowCameraScanner(false);
  }, []);

  const resolveAndOpenBooking = useCallback(async (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) {
      setScanError('أدخل الباركود أولاً');
      return false;
    }

    setScanLoading(true);
    setScanError('');
    try {
      const candidateCodes = Array.from(
        new Set([
          code,
          // Sample barcode fallback: 2603170010-1 => 2603170010
          code.includes('-') ? code.split('-')[0].trim() : '',
          // Also support accidental spaces around dash
          code.replace(/\s+/g, '').includes('-') ? code.replace(/\s+/g, '').split('-')[0].trim() : ''
        ].filter(Boolean))
      );

      let items = [];
      for (const candidate of candidateCodes) {
        const response = await bookingsAPI.getAll({ search: candidate, page: 1, limit: 20 });
        const chunk = response?.data?.data?.items || [];
        if (Array.isArray(chunk) && chunk.length > 0) {
          items = chunk;
          break;
        }
      }

      const exact = items.find((b) =>
        candidateCodes.includes(String(b?.barcode_no || '').trim()) ||
        candidateCodes.includes(String(b?.booking_no || '').trim()) ||
        String(b?._id || '').trim() === code
      );

      const target = exact || (items.length === 1 ? items[0] : null);
      if (!target?._id) {
        throw new Error('لم يتم العثور على حجز مطابق لهذا الباركود');
      }

      stopCameraScan();
      setShowScanModal(false);
      setScanValue('');
      navigate(`/bookings/${target._id}`);
      return true;
    } catch (error) {
      setScanError(error?.response?.data?.message || error.message || 'تعذر العثور على الحجز');
      return false;
    } finally {
      setScanLoading(false);
    }
  }, [navigate, stopCameraScan]);

  const handleSubmitScan = async (valueOverride = null) => {
    const code = valueOverride !== null ? valueOverride : scanValue;
    await resolveAndOpenBooking(code);
  };

  const startCameraScan = useCallback(async () => {
    setCameraError('');
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        setCameraError('الكاميرا غير مدعومة في هذا المتصفح');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });
      cameraStreamRef.current = stream;
      setShowCameraScanner(true);

      if (scanVideoRef.current) {
        scanVideoRef.current.srcObject = stream;
        await scanVideoRef.current.play();
      }

      if (!('BarcodeDetector' in window)) {
        setCameraError('تم فتح الكاميرا، لكن كشف الباركود التلقائي غير مدعوم هنا. استخدم جهاز المسح أو الإدخال اليدوي.');
        return;
      }

      const detector = new window.BarcodeDetector({
        formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code']
      });

      const tick = async () => {
        if (!scanVideoRef.current || !showScanModal || !cameraStreamRef.current) return;
        try {
          const barcodes = await detector.detect(scanVideoRef.current);
          if (Array.isArray(barcodes) && barcodes.length > 0) {
            const rawValue = String(barcodes[0].rawValue || '').trim();
            const now = Date.now();
            if (
              rawValue &&
              (rawValue !== lastDetectedRef.current.code || (now - lastDetectedRef.current.at) > 1500)
            ) {
              lastDetectedRef.current = { code: rawValue, at: now };
              setScanValue(rawValue);
              await resolveAndOpenBooking(rawValue);
              return;
            }
          }
        } catch (e) {
          // Ignore per-frame detector errors and keep scanning.
        }
        scanRafRef.current = requestAnimationFrame(tick);
      };

      scanRafRef.current = requestAnimationFrame(tick);
    } catch (error) {
      setCameraError('تعذر فتح الكاميرا. تأكد من الصلاحية أو جرّب متصفح Chrome.');
      stopCameraScan();
    }
  }, [resolveAndOpenBooking, showScanModal, stopCameraScan]);

  useEffect(() => {
    if (showScanModal && scanInputRef.current) {
      scanInputRef.current.focus();
    }
    if (!showScanModal) {
      stopCameraScan();
    }
  }, [showScanModal, stopCameraScan]);

  useEffect(() => () => stopCameraScan(), [stopCameraScan]);

  const dropdownVariants = {
    hidden: { opacity: 0, y: -5, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  // Role display names
  const roleNames = {
    owner: 'مدير النظام',
    lab_supervisor: 'مشرف المختبر',
    lab_specialist: 'أخصائي مختبر',
    receptionist: 'موظف استقبال',
    contractor_lab: 'مختبر متعاقد',
  };

  // Treat 1030-1350 as compact navbar range to avoid top-nav overlap.
  const isCompactNav = viewportWidth >= 1030 && viewportWidth <= 1350;

  return (
    <>
    <SubscriptionBanner />
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm"
    >
      <div className="w-full px-4 xl:px-6">
        <div className="flex items-center h-[60px]" ref={navRef}>
          {/* Right Section: Logo + Menu */}
          <div className="flex items-center flex-1 gap-2">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex-shrink-0 cursor-pointer flex items-center gap-2"
              onClick={() => navigate('/')}
            >
              {logoUrl ? (
                <img src={logoUrl} alt={labNameAr || 'Logo'} className="h-10 w-auto object-contain" />
              ) : (
                <img src="/listech-logo.svg" alt="LIS Techs" className="h-10 w-auto object-contain" />
              )}
            </motion.div>

            {/* Desktop Menu */}
            {!isCompactNav && (
              <div className="hidden lg:flex items-center">
                {mainMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                const isOpen = openDropdown === item.id;

                return (
                  <div key={item.id} className="relative">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleMenuClick(item)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-md text-[13px] font-medium transition-all duration-200 whitespace-nowrap ${
                        active
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span>{item.label}</span>
                      {item.hasDropdown && (
                        <ChevronDown 
                          className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
                        />
                      )}
                    </motion.button>

                    {/* Dropdown */}
                    <AnimatePresence>
                      {item.hasDropdown && isOpen && (
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={dropdownVariants}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full right-0 mt-1 w-52 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
                        >
                          <div className="py-1">
                            {item.subItems.map((subItem, index) => {
                              const SubIcon = subItem.icon;
                              const isSubActive = location.pathname === subItem.path;
                              return (
                                <motion.button
                                  key={subItem.id}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.03 }}
                                  onClick={() => handleSubItemClick(subItem)}
                                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                    isSubActive
                                      ? 'bg-blue-50 text-blue-600'
                                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                  }`}
                                >
                                  <SubIcon className="w-4 h-4" />
                                  <span>{subItem.label}</span>
                                </motion.button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              </div>
            )}
          </div>

          {/* Left Section: Notifications + Profile */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Scan */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleOpenScan}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
              title="Scan Barcode"
            >
              <ScanLine className="w-4 h-4" />
              <span className="text-xs font-semibold hidden sm:inline">Scan</span>
            </motion.button>

            {/* Settings (icon-only) */}
            {settingsMenuFiltered && (
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setOpenDropdown(openDropdown === settingsMenuFiltered.id ? null : settingsMenuFiltered.id)}
                  className={`p-2 rounded-lg transition-colors ${
                    isActive(settingsMenuFiltered)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                  title="الإعدادات"
                >
                  <Settings className="w-5 h-5" />
                </motion.button>

                <AnimatePresence>
                  {openDropdown === settingsMenuFiltered.id && (
                    <motion.div
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      variants={dropdownVariants}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
                    >
                      <div className="py-1">
                        {settingsMenuFiltered.subItems.map((subItem, index) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <motion.button
                              key={subItem.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.03 }}
                              onClick={() => handleSubItemClick(subItem)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                isSubActive
                                  ? 'bg-blue-50 text-blue-600'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <SubIcon className="w-4 h-4" />
                              <span>{subItem.label}</span>
                            </motion.button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-1.5 rounded-lg text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadChatCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center">
                    {unreadChatCount > 99 ? '99+' : unreadChatCount}
                  </span>
                )}
              </motion.button>

              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
                  >
                    <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-l from-blue-50 to-white">
                      <h3 className="text-sm font-semibold text-gray-800">الإشعارات</h3>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 && (
                        <div className="px-4 py-6 text-center text-sm text-slate-500">لا توجد رسائل غير مقروءة</div>
                      )}
                      {notifications.map((notif) => (
                        <div 
                          key={notif.id} 
                          onClick={() => {
                            setShowNotifications(false);
                            navigate('/lab/chat');
                          }}
                          className={`px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${
                            notif.unread ? 'bg-blue-50/30' : ''
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {notif.unread && (
                              <span className="w-2 h-2 mt-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                            <div className={notif.unread ? '' : 'mr-4'}>
                              <p className="text-sm text-gray-700">{notif.text}</p>
                              {notif.important && (
                                <p className="text-[11px] text-amber-700 font-semibold mt-1">رسالة مهمة</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">{notif.time}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100">
                      <button
                        onClick={() => {
                          setShowNotifications(false);
                          navigate('/lab/chat');
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium w-full text-center"
                      >
                        فتح المحادثات الداخلية
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Profile with Branch Selector */}
            <div className="relative" ref={userMenuRef}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="hidden sm:flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-transparent hover:border-gray-100"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                  {userInitials}
                </div>
                <div className="hidden 2xl:block text-right">
                  <p className="text-sm font-medium text-gray-800 leading-tight">{userName}</p>
                  <p className="text-xs text-gray-400 leading-tight">
                    {selectedBranch?.branch_name || roleNames[userRole] || userRole}
                  </p>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </motion.button>

              {/* User Dropdown Menu */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
                  >
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-l from-emerald-50 to-white">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold">
                          {userInitials}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{userName}</p>
                          <p className="text-xs text-gray-500">{roleNames[userRole] || userRole}</p>
                        </div>
                      </div>
                    </div>

                    {/* Branch Selector */}
                    {canSwitchBranch && branches.length > 0 && (
                      <div className="border-b border-gray-100">
                        <button
                          onClick={() => setShowBranchSelector(!showBranchSelector)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <GitBranch className="w-4 h-4 text-gray-500" />
                            <div className="text-right">
                              <p className="text-xs text-gray-400">الفرع الحالي</p>
                              <p className="text-sm font-medium text-gray-700">
                                {selectedBranch?.branch_name || 'اختر فرع'}
                              </p>
                            </div>
                          </div>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showBranchSelector ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Branch List */}
                        <AnimatePresence>
                          {showBranchSelector && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="max-h-48 overflow-y-auto bg-gray-50 border-t border-gray-100">
                                {branches.map((branch) => (
                                  <button
                                    key={branch._id}
                                    onClick={() => handleBranchSelect(branch)}
                                    className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-100 transition-colors ${
                                      selectedBranch?._id === branch._id ? 'bg-blue-50' : ''
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-2 h-2 rounded-full ${branch.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                      <div className="text-right">
                                        <p className="text-sm text-gray-700">{branch.branch_name}</p>
                                        <p className="text-xs text-gray-400">{branch.city}</p>
                                      </div>
                                    </div>
                                    {selectedBranch?._id === branch._id && (
                                      <Check className="w-4 h-4 text-blue-600" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {/* Current Branch for non-switching users */}
                    {!canSwitchBranch && selectedBranch && (
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center gap-3">
                          <GitBranch className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-xs text-gray-400">الفرع</p>
                            <p className="text-sm font-medium text-gray-700">{selectedBranch.branch_name}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Subscription link (owner only) */}
                    {userRole === 'owner' && (
                      <button
                        onClick={() => { navigate('/settings/subscription'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                      >
                        <CreditCard className="w-4 h-4" />
                        <span className="text-sm font-medium">اشتراكي</span>
                      </button>
                    )}

                    {/* Logout */}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm font-medium">تسجيل الخروج</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Menu Toggle */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`${isCompactNav ? '' : 'lg:hidden'} p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors`}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`${isCompactNav ? '' : 'lg:hidden'} bg-white border-t border-gray-100 overflow-hidden`}
          >
            <div className="px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">
              {[...mainMenuItems, ...(settingsMenuFiltered ? [settingsMenuFiltered] : [])].map((item) => {
                const Icon = item.icon;
                const isOpen = openDropdown === item.id;

                return (
                  <div key={item.id}>
                    <button
                      onClick={() => handleMenuClick(item)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item)
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </div>
                      {item.hasDropdown && (
                        <ChevronDown 
                          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                        />
                      )}
                    </button>

                    <AnimatePresence>
                      {item.hasDropdown && isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden mr-4 border-r-2 border-gray-100"
                        >
                          {item.subItems.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <button
                                key={subItem.id}
                                onClick={() => handleSubItemClick(subItem)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                  location.pathname === subItem.path
                                    ? 'text-blue-600 bg-blue-50/50'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                              >
                                <SubIcon className="w-4 h-4" />
                                <span>{subItem.label}</span>
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              {/* Mobile User Profile */}
              <div className="pt-4 mt-4 border-t border-gray-100 sm:hidden">
                <div className="flex items-center gap-3 px-4 py-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold">
                    {userInitials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{userName}</p>
                    <p className="text-xs text-gray-400">{selectedBranch?.branch_name || roleNames[userRole]}</p>
                  </div>
                </div>

                {/* Mobile Branch Selector */}
                {canSwitchBranch && branches.length > 1 && (
                  <div className="px-4 py-2">
                    <p className="text-xs text-gray-400 mb-2">تبديل الفرع</p>
                    <div className="space-y-1">
                      {branches.map((branch) => (
                        <button
                          key={branch._id}
                          onClick={() => handleBranchSelect(branch)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                            selectedBranch?._id === branch._id 
                              ? 'bg-blue-50 text-blue-600' 
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <span>{branch.branch_name}</span>
                          {selectedBranch?._id === branch._id && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mobile Logout */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg mt-2"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">تسجيل الخروج</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scan Barcode Modal */}
      <AnimatePresence>
        {showScanModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
            onClick={() => {
              if (scanLoading) return;
              stopCameraScan();
              setShowScanModal(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.96, y: 6 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 6 }}
              className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                  <ScanLine className="w-4 h-4 text-emerald-600" />
                  مسح باركود الحجز
                </h3>
                <button
                  onClick={() => {
                    if (scanLoading) return;
                    stopCameraScan();
                    setShowScanModal(false);
                  }}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-3">
                <p className="text-xs text-gray-500">
                  امسح الباركود أو الصق/اكتب رقم الحجز ثم اضغط Enter.
                </p>
                <input
                  ref={scanInputRef}
                  value={scanValue}
                  onChange={(e) => setScanValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmitScan();
                  }}
                  placeholder="Barcode / Booking No"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500"
                  dir="ltr"
                />
                {scanError ? (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                    {scanError}
                  </div>
                ) : null}

                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => (showCameraScanner ? stopCameraScan() : startCameraScan())}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    {showCameraScanner ? 'إيقاف الكاميرا' : 'فتح الكاميرا'}
                  </button>
                  <span className="text-[11px] text-gray-500">
                    يدعم جهاز المسح + الكاميرا
                  </span>
                </div>

                {cameraError ? (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {cameraError}
                  </div>
                ) : null}

                {showCameraScanner && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-black">
                    <video
                      ref={scanVideoRef}
                      className="w-full h-48 object-cover"
                      muted
                      playsInline
                      autoPlay
                    />
                  </div>
                )}
              </div>

              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => {
                    stopCameraScan();
                    setShowScanModal(false);
                  }}
                  disabled={scanLoading}
                  className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSubmitScan}
                  disabled={scanLoading}
                  className="px-4 py-2 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {scanLoading ? 'جاري البحث...' : 'فتح'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
    </>
  );
};

export default Navbar;
